import { isHttpSecure } from "lib/utils/utils";
import { joinUrl } from "lib/utils/connection";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { getAuthToken } from "lib/utils/auth";
import { authedFetch } from "lib/utils/authedFetch";
import {
    messageFromFailedResponse,
    parseFastApiDetail,
} from "lib/utils/userFacingApiError";

/** HTTP 360 session API on the robot (r2c2_360 FastAPI, default port 12411). */

const DEFAULT_INSTA360_PORT = "12411";

export type Insta360State = {
    ok?: boolean;
    state?: string;
};

export type Insta360Wifi = {
    ok?: boolean;
    ssid?: string;
    host?: string;
    has_password?: boolean;
};

async function parseJson(res: Response, fallback: string): Promise<unknown> {
    const text = await res.text();
    let data: unknown = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        data = { raw: text };
    }
    if (!res.ok) {
        const detail =
            typeof data === "object" && data !== null
                ? parseFastApiDetail(data as { detail?: unknown }) ||
                  ("detail" in data ? String((data as { detail: unknown }).detail) : text)
                : text || res.statusText;
        throw new Error(messageFromFailedResponse(res.status, detail, fallback));
    }
    return data;
}

export function getInsta360BaseUrl(
    selectedArcHost?: string | null,
    isSecure: boolean = isHttpSecure(),
    arcId?: string | null
): string | null {
    const explicit = process.env.REACT_APP_INSTA360_BASE_URL?.trim();
    if (explicit) {
        return explicit.replace(/\/$/, "");
    }
    const port = process.env.REACT_APP_INSTA360_PORT?.trim() || DEFAULT_INSTA360_PORT;
    const proxyHost =
        selectedArcHost?.trim() || process.env.REACT_APP_INSTA360_HOST?.trim();
    const host =
        proxyHost ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) {
        return null;
    }
    const protocol = isSecure ? "https" : "http";
    const gatewayBase = gatewayServiceBaseUrl("insta360", arcId, proxyHost);
    if (gatewayBase) return gatewayBase;
    if (arcId) return null;
    return `${protocol}://${host}:${port}`;
}

function withGatewayAuthToken(url: string): string {
    const token = getAuthToken();
    if (!token) return url;
    const u = new URL(url);
    u.searchParams.set("token", token);
    return u.toString();
}

/** JPEG thumbnail for an INSV (`<img src>` — token as query, not Authorization). */
export function getInsta360PreviewUrl(baseUrl: string, filename: string): string {
    return withGatewayAuthToken(
        joinUrl(baseUrl, `/recordings/preview/${encodeURIComponent(filename)}`)
    );
}

/** H.264 MP4 for `<video src>` (converted on the robot). */
export function getInsta360PlaybackUrl(baseUrl: string, filename: string): string {
    return withGatewayAuthToken(
        joinUrl(baseUrl, `/recordings/playback/${encodeURIComponent(filename)}`)
    );
}

export async function fetchInsta360RecordingBlob(
    baseUrl: string,
    filename: string
): Promise<Blob> {
    const res = await authedFetch(
        joinUrl(baseUrl, `/recordings/file/${encodeURIComponent(filename)}`)
    );
    if (!res.ok) {
        await parseJson(res, "Couldn't download the 360 video.");
        throw new Error("Couldn't download the 360 video.");
    }
    return res.blob();
}

export async function getInsta360State(baseUrl: string): Promise<Insta360State> {
    const res = await authedFetch(joinUrl(baseUrl, "/state"));
    return (await parseJson(res, "Couldn't read 360 camera status.")) as Insta360State;
}

export async function postInsta360Connect(baseUrl: string): Promise<Insta360State> {
    const res = await authedFetch(joinUrl(baseUrl, "/connect"), { method: "POST" });
    return (await parseJson(res, "Couldn't connect the 360 camera.")) as Insta360State;
}

export async function postInsta360Disconnect(baseUrl: string): Promise<Insta360State> {
    const res = await authedFetch(joinUrl(baseUrl, "/disconnect"), { method: "POST" });
    return (await parseJson(
        res,
        "Couldn't disconnect the 360 camera."
    )) as Insta360State;
}

export async function postInsta360RecordStart(baseUrl: string): Promise<Insta360State> {
    const res = await authedFetch(joinUrl(baseUrl, "/record/start"), { method: "POST" });
    return (await parseJson(res, "Couldn't start 360 recording.")) as Insta360State;
}

export async function postInsta360RecordStop(baseUrl: string): Promise<Insta360State> {
    const res = await authedFetch(joinUrl(baseUrl, "/record/stop"), { method: "POST" });
    return (await parseJson(res, "Couldn't stop 360 recording.")) as Insta360State;
}

export async function getInsta360Wifi(baseUrl: string): Promise<Insta360Wifi> {
    const res = await authedFetch(joinUrl(baseUrl, "/wifi"));
    return (await parseJson(res, "Couldn't load 360 Wi-Fi settings.")) as Insta360Wifi;
}

export async function putInsta360Wifi(
    baseUrl: string,
    body: { ssid: string; password: string; host: string }
): Promise<void> {
    const res = await authedFetch(joinUrl(baseUrl, "/wifi"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    await parseJson(res, "Could not save credentials");
}

export type Insta360RecordingFile = {
    name: string;
    size_bytes: number;
    modified_utc: string;
};

export type Insta360RecordingsList = {
    ok?: boolean;
    directory?: string;
    files: Insta360RecordingFile[];
};

export type OpenSpaceSettings = {
    ok?: boolean;
    client_id?: string;
    username?: string;
    site_id?: string;
    sheet_id?: string;
    device_id?: string;
    domain?: string;
    has_client_secret?: boolean;
    has_password?: boolean;
};

export async function listInsta360Recordings(baseUrl: string): Promise<Insta360RecordingsList> {
    const res = await authedFetch(joinUrl(baseUrl, "/recordings"), { cache: "no-store" });
    const data = (await parseJson(res, "Couldn't list 360 recordings.")) as Insta360RecordingsList;
    return { ...data, files: Array.isArray(data.files) ? data.files : [] };
}

export type Insta360CameraFile = {
    name: string;
    uri?: string;
    on_robot?: boolean;
};

export async function listInsta360CameraFiles(
    baseUrl: string
): Promise<{ files: Insta360CameraFile[]; state?: string }> {
    const res = await authedFetch(joinUrl(baseUrl, "/camera/files"), { cache: "no-store" });
    const data = (await parseJson(res, "Couldn't list videos on the 360 camera.")) as {
        files?: Insta360CameraFile[];
        state?: string;
    };
    return { files: Array.isArray(data.files) ? data.files : [], state: data.state };
}

export async function postInsta360CameraTransfer(
    baseUrl: string,
    filename: string
): Promise<{ ok?: boolean; path?: string; state?: string }> {
    const res = await authedFetch(joinUrl(baseUrl, "/camera/files/transfer"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename }),
    });
    return (await parseJson(res, "Couldn't transfer the 360 video.")) as {
        ok?: boolean;
        path?: string;
        state?: string;
    };
}

export async function getOpenSpaceSettings(baseUrl: string): Promise<OpenSpaceSettings> {
    const res = await authedFetch(joinUrl(baseUrl, "/openspace"), { cache: "no-store" });
    return (await parseJson(res, "Couldn't load OpenSpace settings.")) as OpenSpaceSettings;
}

export async function putOpenSpaceSettings(
    baseUrl: string,
    body: {
        client_id: string;
        client_secret: string;
        username: string;
        password: string;
        site_id: string;
        sheet_id: string;
        device_id: string;
        domain: string;
    }
): Promise<void> {
    const res = await authedFetch(joinUrl(baseUrl, "/openspace"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    await parseJson(res, "Could not save OpenSpace settings");
}

export type OpenSpaceUploadJob = {
    ok?: boolean;
    id?: string;
    status?: string;
    filename?: string;
    stage?: string;
    bytes_sent?: number;
    bytes_total?: number;
    capture_id?: string;
    message?: string;
    started_at?: string;
    updated_at?: string;
    history?: OpenSpaceUploadJob[];
    network_log_count?: number;
};

export async function getOpenSpaceUpload(baseUrl: string): Promise<OpenSpaceUploadJob> {
    const res = await authedFetch(joinUrl(baseUrl, "/openspace/upload"), { cache: "no-store" });
    return (await parseJson(res, "Couldn't read OpenSpace upload status.")) as OpenSpaceUploadJob;
}

export async function postOpenSpaceUpload(
    baseUrl: string,
    body: { filename: string; capture_name?: string }
): Promise<OpenSpaceUploadJob> {
    const res = await authedFetch(joinUrl(baseUrl, "/openspace/upload"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    return (await parseJson(res, "OpenSpace upload failed.")) as OpenSpaceUploadJob;
}

export async function postOpenSpaceUploadCancel(baseUrl: string): Promise<OpenSpaceUploadJob> {
    const res = await authedFetch(joinUrl(baseUrl, "/openspace/upload/cancel"), {
        method: "POST",
    });
    return (await parseJson(res, "Couldn't cancel OpenSpace upload.")) as OpenSpaceUploadJob;
}

export async function downloadOpenSpaceNetworkLog(
    baseUrl: string
): Promise<{ blob: Blob; filename: string }> {
    const res = await authedFetch(joinUrl(baseUrl, "/openspace/upload/network-log"), {
        cache: "no-store",
    });
    if (!res.ok) {
        await parseJson(res, "Couldn't download the OpenSpace network log.");
        throw new Error("Couldn't download the OpenSpace network log.");
    }
    const blob = await res.blob();
    const header = res.headers.get("Content-Disposition") || "";
    const match = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(header);
    const filename = match?.[1]
        ? decodeURIComponent(match[1].replace(/["']/g, "").trim())
        : "openspace-network-log.json";
    return { blob, filename };
}
